import * as s from "./CardItemStyled";
import { WidgetCardDetail } from "api/interfaces/widgetInterface.interface";
import { CardItemName, changeStringBottom } from "./CardItem";
import { DashboardCardDetailTable } from "components/atoms/table/AntdTable";
import { Table, TableColumnsType } from "antd";
import { getLevelFullName } from "api/mappers/accountMapper";
import { Level } from "components/atoms/level/Level";
import { FaGears } from "react-icons/fa6";
import { IoVideocam } from "react-icons/io5";
import { FiHardDrive } from "react-icons/fi";
import { formatNumber } from "utils/functions";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useMemo } from "react";

interface Props {
  name: CardItemName;
  isType?: boolean;
  details: WidgetCardDetail[];
}

const changeIconToAlert = (alert: string) => {
  switch (alert) {
    case "System":
      return <FaGears size={16} />;
    case "Disk":
      return <FiHardDrive size={16} />;
    case "Video":
      return <IoVideocam size={16} />;
    default:
      return "";
  }
};

const changeCameraName = (camera: string) => {
  const cameraName = (camera || "").toUpperCase();
  switch (cameraName) {
    case "SPECTRUM":
      return "SPECTRUM";
    case "VMAX-IP":
      return "VMAX-IP";
    case "VMAX-A1":
      return "VMAX-A1";
    case "COVA":
      return "COVA";
    default:
      return cameraName;
  }
};

const getTotalCount = (details: WidgetCardDetail[], column: string) => {
  return (details || []).reduce(
    (acc, curr) =>
      acc + ((curr[column as keyof WidgetCardDetail] as number) || 0),
    0
  );
};

export default function CardItemDetail(props: Props): JSX.Element {
  const { isEnabled }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const columns: TableColumnsType<WidgetCardDetail> = [
    {
      title: "",
      dataIndex: "type",
      render: (value) => changeCameraName(value),
      width: 150,
    },
    {
      title: "QTY",
      dataIndex: "count",
      render: (value) => formatNumber(value),
      align: "right",
    },
    {
      title: "CAM",
      dataIndex: "camCount",
      render: (value) => formatNumber(value),
      align: "right",
    },
    {
      title: "Alert",
      dataIndex: "alertCount",
      render: (value) => formatNumber(value),
      align: "right",
      hidden: !isEnabled,
    },
  ];
  const recorderTypeColumns: TableColumnsType<WidgetCardDetail> = [
    {
      title: "",
      dataIndex: "type",
      render: (value) => changeCameraName(value),
      width: 150,
    },
    {
      title: "QTY",
      dataIndex: "recorder",
      render: (value) => formatNumber(value),
      align: "right",
    },
    {
      title: "CAM",
      dataIndex: "camera",
      render: (value) => formatNumber(value),
      align: "right",
    },
    {
      title: "Alert",
      dataIndex: "alert",
      render: (value) => formatNumber(value),
      align: "right",
      hidden: !isEnabled,
    },
  ];

  const cameraColumns: TableColumnsType<WidgetCardDetail> = [
    {
      title: "",
      dataIndex: "type",
      render: (value) => changeCameraName(value),
      width: 150,
    },
    {
      title: "Total",
      dataIndex: "count",
      render: (value) => formatNumber(value),
      align: "right",
    },
    {
      title: "Alert",
      dataIndex: "alertCount",
      render: (value) => formatNumber(value),
      align: "right",
      hidden: !isEnabled,
    },
  ];

  const alertColumns: TableColumnsType<WidgetCardDetail> = [
    {
      title: "",
      dataIndex: "type",
      render: (value) => (
        <s.ColumnTitle>
          {changeIconToAlert(value)}
          {value}
        </s.ColumnTitle>
      ),
    },
    {
      title: "",
      dataIndex: "count",
      render: (value) => formatNumber(value),
      align: "right",
    },
  ];

  const accountColumns: TableColumnsType<WidgetCardDetail> = [
    {
      title: "",
      dataIndex: "type",
      render: (value) => (
        <s.ColumnTitle>
          <Level level={getLevelFullName(value)} /> {changeStringBottom(value)}
        </s.ColumnTitle>
      ),
    },
    {
      title: "",
      dataIndex: "count",
      render: (value) => formatNumber(value),
      align: "right",
    },
  ];

  const recorderSummaryColumns = useMemo(() => {
    return !isEnabled
      ? ["recorder", "camera"]
      : ["recorder", "camera", "alert"];
  }, [isEnabled]);
  const cameraSummaryColumns = useMemo(() => {
    return !isEnabled
      ? ["count", "camCount"]
      : ["count", "camCount", "alertCount"];
  }, [isEnabled]);
  const alertSummaryColumns = useMemo(() => {
    return !isEnabled ? ["count"] : ["count", "alertCount"];
  }, [isEnabled]);

  const contents = {
    recorderCount: (
      <DashboardCardDetailTable
        columns={props.isType ? recorderTypeColumns : columns}
        dataSource={props.details}
        summary={(pageData) => (
          <CardItemDetailSummary
            details={props.details}
            columns={
              props.isType ? recorderSummaryColumns : cameraSummaryColumns
            }
          />
        )}
      />
    ),
    cameraCount: (
      <DashboardCardDetailTable
        columns={cameraColumns}
        dataSource={props.details}
        summary={(pageData) => (
          <CardItemDetailSummary
            details={props.details}
            columns={alertSummaryColumns}
          />
        )}
      />
    ),
    alertCount: (
      <DashboardCardDetailTable
        columns={alertColumns}
        dataSource={props.details}
        summary={(pageData) => (
          <CardItemDetailSummary details={props.details} columns={["count"]} />
        )}
      />
    ),
    organizationCount: (
      <DashboardCardDetailTable
        columns={accountColumns}
        dataSource={props.details}
        showHeader={false}
        summary={(pageData) => (
          <CardItemDetailSummary details={props.details} columns={["count"]} />
        )}
      />
    ),
  };
  return <s.DetailWrapper>{contents[props.name]}</s.DetailWrapper>;
}

interface SummaryProps {
  details: WidgetCardDetail[];
  columns: string[];
}

export const CardItemDetailSummary = (props: SummaryProps) => {
  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}></Table.Summary.Cell>
      {(props.columns || []).map((column, index) => (
        <Table.Summary.Cell
          key={`summary-${column}-${index}`}
          index={index + 1}
        >
          <span
            className={`total-count ${
              index === (props.columns || []).length - 1 ? "alert" : ""
            }`}
          >
            {formatNumber(getTotalCount(props.details, column))}
          </span>
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );
};
